import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControlLabel,
  Paper,
  Switch,
  TextField,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import api from "../../../config/api";

const DEFAULT_FORM = {
  message: "",
  buttonLabel: "",
  link: "",
  visible: true,
};

export default function BannerSettings() {
  const [form, setForm] = useState(DEFAULT_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get("/api/admin/settings/banner")
      .then((res) => {
        if (!cancelled && res?.data) {
          setForm({
            message: res.data.message ?? "",
            buttonLabel: res.data.buttonLabel ?? "",
            link: res.data.link ?? "",
            visible: res.data.visible !== false,
          });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFeedback({ type: "error", text: "Failed to load banner settings." });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleChange = (field) => (event) => {
    const value = field === "visible" ? event.target.checked : event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      await api.put("/api/admin/settings/banner", form);
      setFeedback({ type: "success", text: "Banner settings saved." });
    } catch {
      setFeedback({ type: "error", text: "Failed to save banner settings." });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 720, mx: "auto" }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
          Homepage Banner
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Controls the promotional banner shown directly below the navigation on customer pages.
        </Typography>

        {feedback && (
          <Alert severity={feedback.type} sx={{ mb: 2 }}>
            {feedback.text}
          </Alert>
        )}

        <FormControlLabel
          control={<Switch checked={form.visible} onChange={handleChange("visible")} />}
          label="Show banner"
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          label="Message"
          value={form.message}
          onChange={handleChange("message")}
          multiline
          minRows={2}
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          label="Button label (optional)"
          value={form.buttonLabel}
          onChange={handleChange("buttonLabel")}
          sx={{ mb: 2 }}
          helperText="Leave blank to hide the button."
        />

        <TextField
          fullWidth
          label="Button link (optional)"
          value={form.link}
          onChange={handleChange("link")}
          sx={{ mb: 3 }}
          helperText='Internal route (e.g. "/seasonal_fruits/seasonal_produce") or full URL.'
        />

        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          sx={{ backgroundColor: "#6B8E23", "&:hover": { backgroundColor: "#5a7a1f" } }}
        >
          {saving ? "Saving..." : "Save"}
        </Button>
      </Paper>
    </Box>
  );
}
